//import React, { useContext, useEffect } from 'react';
import { BusinessContext } from '@/apps/providers/BusinessContext';


/**
 * Analytics Hook for tracking chat interactions
 * Supports Google Analytics 4, custom analytics, and A/B testing
 */

const isProduction = process.env.NODE_ENV === 'production';

// Analytics events that will be tracked
const ANALYTICS_EVENTS = {
  // Session Events
  CHAT_OPENED: 'chat_opened',
  CHAT_CLOSED: 'chat_closed',
  CHAT_SESSION_STARTED: 'chat_session_started',
  CHAT_SESSION_RESUMED: 'chat_session_resumed',
  CHAT_SESSION_EXPIRED: 'chat_session_expired',

  // Conversation Events
  MESSAGE_SENT: 'message_sent',
  MESSAGE_RECEIVED: 'message_received',
  MESSAGE_SUGGESTION_TAPPED: 'message_suggested_tapped',
  MESSAGE_FOLLOWUP_TAPPED: 'message_followup_tapped',
  VOICE_INPUT_STARTED: 'voice_input_started',
  VOICE_INPUT_COMPLETED: 'voice_input_completed',
  VOICE_INPUT_FAILED: 'voice_input_failed',

  // Intent Events
  INTENT_SEARCH: 'intent_search',
  INTENT_ORDER: 'intent_order',
  INTENT_CART: 'intent_cart',
  INTENT_REORDER: 'intent_reorder',
  INTENT_DIETARY: 'intent_dietary',
  INTENT_HELP: 'intent_help',
  INTENT_GENERAL: 'intent_general',
  INTENT_CHECKOUT: 'intent_checkout',

  // Product Events
  PRODUCT_VIEWED_IN_CHAT: 'product_viewed_in_chat',
  PRODUCT_SELECTED_FROM_MULTIPLE: 'product_selected_from_multiple',
  PRODUCT_CONFIGURATION_STARTED: 'product_configuration_started',
  PRODUCT_SIZE_SELECTED: 'product_size_selected',
  PRODUCT_OPTION_SELECTED: 'product_option_selected',
  PRODUCT_COMBO_SELECTED: 'product_combo_selected',
  PRODUCT_ADDON_SELECTED: 'product_addon_selected',
  PRODUCT_CONFIGURATION_COMPLETED: 'product_configuration_completed',
  PRODUCT_CONFIGURATION_ABANDONED: 'product_configuration_abandoned',

  // Cart Events
  CART_ADD_VIA_CHAT: 'cart_add_via_chat',
  CART_ADD_ATTEMPT_FAILED: 'cart_add_attempt_failed',
  CART_VALUE_CONFIRMED: 'cart_value_confirmed',

  // Error Events
  ERROR_API_INIT_FAILED: 'error_api_init_failed',
  ERROR_API_MESSAGE_FAILED: 'error_api_message_failed',
  ERROR_API_VALIDATE_FAILED: 'error_api_validate_failed',
  ERROR_NETWORK_TIMEOUT: 'error_network_timeout',
  ERROR_SESSION_INVALID: 'error_session_invalid',
  ERROR_PRODUCT_NOT_FOUND: 'error_product_not_found',
  ERROR_CONFIGURATION_INVALID: 'error_configuration_invalid',

  // Performance Events
  PERFORMANCE_FIRST_MESSAGE_TIME: 'performance_first_message_time',
  PERFORMANCE_API_RESPONSE_TIME: 'performance_api_response_time',
  PERFORMANCE_WIDGET_LOAD_TIME: 'performance_widget_load_time',
  PERFORMANCE_VOICE_INPUT_TIME: 'performance_voice_input_time',
};

/**
 * Custom analytics hook
 */
export const useAnalytics = () => {

   const { selectedBusiness: business, selectedMenu: menu } = React.useContext(BusinessContext);
 const [sessionId, setSessionId] = React.useState(null);

  /**
   * Track an analytics event
   */
  const trackEvent = (eventName, properties = {}) => {
    if (!isProduction) {
      console.log('[Analytics]', eventName, properties);
      return;
    }

    // Send to Google Analytics 4
    if (window.gtag) {
      window.gtag('event', eventName, {
        ...properties,
        custom_map: {
          businessId: business?.id,
          menuId: menu?.id,
          session_id: sessionId,
        },
      });
    }

    // Send to custom analytics endpoint (if configured)
    sendToCustomAnalytics(eventName, properties);
  };

  /**
   * Track chat opened
   */
  const trackChatOpened = () => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      timestamp: new Date().toISOString(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      is_mobile: /Mobile|Android|iPhone/i.test(navigator.userAgent),
    };

    trackEvent(ANALYTICS_EVENTS.CHAT_OPENED, properties);
  };

  /**
   * Track chat closed
   */
  const trackChatClosed = (duration, messageCount) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      duration_seconds: duration,
      message_count: messageCount,
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.CHAT_CLOSED, properties);
  };

  /**
   * Track session started
   */
  const trackSessionStarted = (isNewSession, sessionId) => {
    setSessionId(sessionId)
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      sessionId: sessionId,
      is_new_session: isNewSession,
      timestamp: new Date().toISOString(),
    };

    trackEvent(
      isNewSession
        ? ANALYTICS_EVENTS.CHAT_SESSION_STARTED
        : ANALYTICS_EVENTS.CHAT_SESSION_RESUMED,
      properties
    );
  };

  /**
   * Track message sent
   */
  const trackMessageSent = (message, intent) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      message_length: message.length,
      intent: intent,
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.MESSAGE_SENT, properties);
  };

  /**
   * Track message received
   */
  const trackMessageReceived = (message, intent, hasProducts, hasFollowUp) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      message_length: message.length,
      intent: intent,
      has_products: hasProducts,
      has_follow_up: hasFollowUp,
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.MESSAGE_RECEIVED, properties);
  };

  /**
   * Track product viewed in chat
   */
  const trackProductViewed = (product, source) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      product_id: product.id,
      product_name: product.name,
      product_price: product.price,
      source: source, // 'search', 'suggestion', 'configuration'
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.PRODUCT_VIEWED_IN_CHAT, properties);
  };

  /**
   * Track product configuration started
   */
  const trackConfigurationStarted = (product) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      product_id: product.id,
      product_name: product.name,
      has_sizes: product.sizes?.length > 0,
      has_options: product.option_groups?.length > 0,
      has_combos: product.combos?.length > 0,
      has_addons: product.addons?.length > 0,
      complexity_score: calculateProductComplexity(product),
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.PRODUCT_CONFIGURATION_STARTED, properties);
  };

  /**
   * Track product configuration completed
   */
  const trackConfigurationCompleted = (product, duration, stepsCount) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      product_id: product.id,
      product_name: product.name,
      final_price: calculateProductPrice(product),
      duration_seconds: duration,
      steps_count: stepsCount,
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.PRODUCT_CONFIGURATION_COMPLETED, properties);
  };

  /**
   * Track cart add via chat
   */
  const trackCartAddViaChat = (cartItem, source) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      product_id: cartItem.id,
      product_name: cartItem.name,
      quantity: cartItem.quantity,
      price: cartItem.price,
      has_options: cartItem.selected_options?.length > 0,
      has_addons: cartItem.selected_addons?.length > 0,
      source: source, // 'chat', 'reorder', 'suggestion'
      timestamp: new Date().toISOString(),
    };

    trackEvent(ANALYTICS_EVENTS.CART_ADD_VIA_CHAT, properties);
  };

  /**
   * Track error
   */
  const trackError = (errorType, errorMessage, errorDetails = {}) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      error_type: errorType,
      error_message: errorMessage,
      ...errorDetails,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    };

    trackEvent(`error_${errorType}`, properties);
  };

  /**
   * Track performance metric
   */
  const trackPerformance = (metricName, value, unit = 'ms') => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      metric_name: metricName,
      value: value,
      unit: unit,
      timestamp: new Date().toISOString(),
    };

    trackEvent(`performance_${metricName}`, properties);
  };

  /**
   * Track funnel step
   */
  const trackFunnelStep = (funnelName, stepName, completed = true) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      funnel_name: funnelName,
      step_name: stepName,
      completed: completed,
      timestamp: new Date().toISOString(),
    };

    trackEvent(`funnel_${funnelName}_${stepName}`, properties);
  };

  /**
   * Track A/B test variant
   */
  const trackAbTestVariant = (testName, variantName) => {
    const properties = {
      businessId: business?.id,
      menuId: menu?.id,
      session_id: sessionId,
      test_name: testName,
      variant_name: variantName,
      timestamp: new Date().toISOString(),
    };

    trackEvent(`ab_test_${testName}`, properties);

    // Store variant in localStorage for consistency
    localStorage.setItem(`ab_test_${testName}`, variantName);
  };

  /**
   * Get A/B test variant
   */
  const getAbTestVariant = (testName, variants) => {
    // Check if user already has a variant assigned
    const storedVariant = localStorage.getItem(`ab_test_${testName}`);
    if (storedVariant) {
      return storedVariant;
    }

    // Assign random variant
    const variantIndex = Math.floor(Math.random() * variants.length);
    const variant = variants[variantIndex];

    trackAbTestVariant(testName, variant);
    return variant;
  };

  return {
    trackEvent,
    trackChatOpened,
    trackChatClosed,
    trackSessionStarted,
    trackMessageSent,
    trackMessageReceived,
    trackProductViewed,
    trackConfigurationStarted,
    trackConfigurationCompleted,
    trackCartAddViaChat,
    trackError,
    trackPerformance,
    trackFunnelStep,
    trackAbTestVariant,
    getAbTestVariant,
    ANALYTICS_EVENTS,
  };
};

/**
 * Send analytics to custom endpoint
 */
const sendToCustomAnalytics = (eventName, properties) => {
  // TODO: Implement custom analytics backend endpoint
  // fetch('/api/v1/analytics/event', {
  //   method: 'POST',
  //   headers: { 'Content-Type': 'application/json' },
  //   body: JSON.stringify({ event: eventName, properties }),
  // }).catch(err => console.error('Analytics error:', err));
};

/**
 * Calculate product complexity score
 */
const calculateProductComplexity = (product) => {
  let score = 0;

  if (product.sizes?.length > 0) score += 1;
  if (product.option_groups?.length > 0) score += product.option_groups.length * 2;
  if (product.combos?.length > 0) score += product.combos.length * 3;
  if (product.addons?.length > 0) score += 1;

  return score;
};

/**
 * Calculate final product price
 */
const calculateProductPrice = (product) => {
  let price = product.price || 0;

  // Add option prices
  if (product.selected_options) {
    product.selected_options.forEach(opt => {
      price += opt.price || 0;
    });
  }

  // Add addon prices
  if (product.selected_addons) {
    product.selected_addons.forEach(addon => {
      price += (addon.price || 0) * (addon.quantity || 0);
    });
  }

  return price;
};

export default useAnalytics;
